import { useState, useRef, useEffect } from 'react';
import Header from './components/Header';
import ConceptMap from './components/ConceptMap';
import CampCard from './components/CampCard';
import CategoryFilters from './components/CategoryFilters';
import SubmitCampModal from './components/SubmitCampModal';
import CampDetailModal from './components/CampDetailModal';
import SummerCalendar from './components/SummerCalendar';
import CheyennesTopPicks from './components/CheyennesTopPicks';
import Logo from '../../components/feature/Logo';
import { camps, summerWeeks } from '../../mocks/camps';
import { momReviews } from '../../mocks/reviews';
import { useVotes } from '../../hooks/useVotes';

export default function HomePage() {
  const [selectedAge, setSelectedAge] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'map' | 'list'>('map');
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [selectedCamp, setSelectedCamp] = useState<typeof camps[0] | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWeek, setSelectedWeek] = useState<number | null>(null);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const { vote, getVotes, hasVoted, topCamps } = useVotes();

  const campResultsRef = useRef<HTMLDivElement>(null);
  const categoryFiltersRef = useRef<HTMLDivElement>(null);

  const scrollToCamps = () => {
    setTimeout(() => {
      campResultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const scrollToFilters = () => {
    setTimeout(() => {
      categoryFiltersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleAgeSelect = (ageGroup: string) => {
    setSelectedAge(ageGroup || null);
    setSelectedCategory(null);
    if (ageGroup) {
      scrollToFilters();
    }
  };

  const handleCategorySelect = (category: string | null) => {
    setSelectedCategory(category);
    if (category) {
      scrollToCamps();
    }
  };

  const handleWeekSelect = (week: number | null) => {
    setSelectedWeek(week);
    if (week !== null) {
      setTimeout(() => {
        campResultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  };

  const filteredCamps = camps.filter(camp => {
    const hasAnyFilter = selectedAge || selectedCategory || searchQuery.trim() || selectedWeek !== null;
    if (!hasAnyFilter) return true;

    let matches = true;

    if (selectedAge) {
      matches = matches && camp.ageGroup === selectedAge;
    }

    if (selectedCategory) {
      if (selectedCategory === 'full-day') {
        matches = matches && camp.duration === 'full-day';
      } else if (selectedCategory === 'half-day') {
        matches = matches && camp.duration === 'half-day';
      } else if (selectedCategory === 'overnight') {
        matches = matches && camp.duration === 'overnight';
      } else if (selectedCategory === 'one-week') {
        matches = matches && true;
      } else {
        matches = matches && camp.category === selectedCategory;
      }
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const inName = camp.name.toLowerCase().includes(q);
      const inCity = camp.city.toLowerCase().includes(q);
      const inCategory = camp.category.toLowerCase().includes(q);
      const inDesc = camp.description.toLowerCase().includes(q);
      matches = matches && (inName || inCity || inCategory || inDesc);
    }

    if (selectedWeek !== null) {
      matches = matches && camp.availableWeeks.includes(selectedWeek);
    }

    return matches;
  });

  const handleReset = () => {
    setSelectedAge(null);
    setSelectedCategory(null);
    setSelectedWeek(null);
  };

  const showCampGrid = selectedAge || selectedCategory || viewMode === 'list' || searchQuery.trim().length > 0 || selectedWeek !== null;

  const ageGroupLabels: Record<string, string> = {
    'preschool': 'Preschool (Ages 3–4)',
    'early-elementary': 'Early Elementary (Ages 5–7)',
    'upper-elementary': 'Upper Elementary (Ages 8–10)',
    'tweens': 'Tweens (Ages 11–13)',
    'teens': 'Teens (Ages 14–17)',
  };

  const ageGroupOrder: Array<typeof camps[0]['ageGroup']> = [
    'preschool',
    'early-elementary',
    'upper-elementary',
    'tweens',
    'teens',
  ];

  const isGroupedView =
    viewMode === 'list' &&
    !selectedAge &&
    !selectedCategory &&
    !searchQuery.trim() &&
    selectedWeek === null;

  const shareUrl = typeof window !== 'undefined' ? window.location.href : 'https://tmmcampguide.com';
  const shareText = 'Check out the TMM Summer Camp Guide — the best resource for Gwinnett County families planning summer camps! 🏕️';

  const handleShareFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
      '_blank',
      'width=600,height=400'
    );
    setIsShareOpen(false);
  };

  const handleShareTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      '_blank',
      'width=600,height=400'
    );
    setIsShareOpen(false);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    });
  };

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebSite',
          '@id': `${siteUrl}/#website`,
          url: siteUrl,
          name: "TMM's Summer Camp Guide",
          alternateName: "The Mommy Mingle Summer Camp Guide",
          description: "Helping Gwinnett Families Plan the Best Summer Ever",
          publisher: {
            '@id': `${siteUrl}/#organization`,
          },
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: `${siteUrl}/?search={search_term_string}`,
            },
            'query-input': 'required name=search_term_string',
          },
          inLanguage: 'en-US',
        },
        {
          '@type': ['Organization', 'LocalBusiness'],
          '@id': `${siteUrl}/#organization`,
          name: 'The Mommy Mingle',
          url: siteUrl,
          logo: {
            '@type': 'ImageObject',
            url: 'https://static.readdy.ai/image/0b5cbc7d1d3a0b59b4f12d58a349c104/c97e6db77242590d6243993853b36aa2.png',
            width: 512,
            height: 512,
          },
          image: 'https://static.readdy.ai/image/0b5cbc7d1d3a0b59b4f12d58a349c104/c97e6db77242590d6243993853b36aa2.png',
          description: 'A community resource connecting Gwinnett County families with the best summer camps for kids ages 3–17.',
          areaServed: {
            '@type': 'AdministrativeArea',
            name: 'Gwinnett County, Georgia',
          },
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Gwinnett County',
            addressRegion: 'GA',
            addressCountry: 'US',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 33.9526,
            longitude: -84.0708,
          },
          sameAs: [],
        },
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/#webpage`,
          url: siteUrl,
          name: "TMM's Summer Camp Guide | Gwinnett County Summer Camps 2026",
          description:
            "TMM's Summer Camp Guide helps Gwinnett families find the best summer camps for kids ages 3–17. Browse STEM, sports, arts, adventure & pre-college camps near Gwinnett County, GA.",
          isPartOf: { '@id': `${siteUrl}/#website` },
          about: { '@id': `${siteUrl}/#organization` },
          inLanguage: 'en-US',
          dateModified: new Date().toISOString().split('T')[0],
          breadcrumb: { '@id': `${siteUrl}/#breadcrumb` },
          speakable: {
            '@type': 'SpeakableSpecification',
            cssSelector: ['h1', 'h2'],
          },
        },
        {
          '@type': 'BreadcrumbList',
          '@id': `${siteUrl}/#breadcrumb`,
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: siteUrl,
            },
          ],
        },
        {
          '@type': 'ItemList',
          '@id': `${siteUrl}/#camp-categories`,
          name: 'Summer Camp Categories in Gwinnett County',
          description: 'Browse summer camp categories available for Gwinnett County families',
          numberOfItems: 5,
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'STEM & Technology Camps' },
            { '@type': 'ListItem', position: 2, name: 'Sports & Athletics Camps' },
            { '@type': 'ListItem', position: 3, name: 'Arts & Performing Arts Camps' },
            { '@type': 'ListItem', position: 4, name: 'Adventure & Outdoor Camps' },
            { '@type': 'ListItem', position: 5, name: 'Pre-College & Leadership Camps' },
          ],
        },
        {
          '@type': 'FAQPage',
          '@id': `${siteUrl}/#faq`,
          mainEntity: [
            {
              '@type': 'Question',
              name: 'What age groups are covered in the TMM Summer Camp Guide?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'The guide covers all age groups from Preschool (Ages 3–4), Early Elementary (Ages 5–7), Upper Elementary (Ages 8–10), Tweens (Ages 11–13), to Teens (Ages 14–17).',
              },
            },
            {
              '@type': 'Question',
              name: 'What types of summer camps are listed in Gwinnett County?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'The guide includes STEM, sports, arts, adventure, leadership, pre-college programs, performing arts, outdoor, and many more camp categories in Gwinnett County and surrounding areas.',
              },
            },
            {
              '@type': 'Question',
              name: 'How can I submit my summer camp to the TMM guide?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Camp owners can click the "Submit Your Camp" button on the website to add their camp to the TMM Summer Camp Guide for Gwinnett families.',
              },
            },
            {
              '@type': 'Question',
              name: 'Are the summer camps in this guide located near Gwinnett County, Georgia?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes, all camps listed in the TMM Summer Camp Guide are located in or near Gwinnett County, Georgia, including surrounding Atlanta metro communities.',
              },
            },
            {
              '@type': 'Question',
              name: 'Is the TMM Summer Camp Guide free to use?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes, the TMM Summer Camp Guide is a free community resource provided by The Mommy Mingle to help Gwinnett families discover and plan summer camps for their children.',
              },
            },
          ],
        },
      ],
    };

    const existingScript = document.getElementById('schema-jsonld');
    if (existingScript) existingScript.remove();

    const script = document.createElement('script');
    script.id = 'schema-jsonld';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('schema-jsonld');
      if (s) s.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-pink-50">
      <Header onSubmitCampClick={() => setIsSubmitModalOpen(true)} />

      {/* Hero Section */}
      <section id="home" className="relative pt-20 pb-20 overflow-hidden">
        {/* Vibrant gradient background matching the graphic */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#E91E8C] via-[#D946A6] to-[#F5C518]"></div>
        
        {/* Organic blob shapes for playful feel */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-[#3D1A6E] rounded-full opacity-20 blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#F5C518] rounded-full opacity-30 blur-3xl translate-x-1/3 translate-y-1/3"></div>
          <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] bg-white rounded-full opacity-10 blur-2xl -translate-x-1/2 -translate-y-1/2"></div>
        </div>

        {/* Texture overlay for depth */}
        <div className="absolute inset-0 opacity-30 mix-blend-overlay" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100\' height=\'100\' filter=\'url(%23noise)\' opacity=\'0.4\'/%3E%3C/svg%3E")' }}></div>

        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <h1 className="text-6xl md:text-7xl font-black text-white mb-4 leading-tight tracking-tight drop-shadow-lg" style={{ fontFamily: 'Fredoka, Poppins, system-ui, sans-serif' }}>
            TMM's Summer Camp Guide
          </h1>
          <p className="text-2xl md:text-3xl font-bold text-white mb-3 max-w-3xl mx-auto drop-shadow-md">
            Helping Gwinnett Families Plan the Best Summer Ever
          </p>
          <p className="text-lg text-white/95 mb-10 max-w-2xl mx-auto leading-relaxed drop-shadow-sm">
            Discover, compare, and plan the best summer camps across Gwinnett and nearby communities.
          </p>

          {/* View Toggle - White pills with magenta active state */}
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <button
              onClick={() => setViewMode('map')}
              className={`px-7 py-3.5 rounded-full font-bold transition-all text-base whitespace-nowrap shadow-lg ${
                viewMode === 'map'
                  ? 'bg-white text-[#E91E8C] scale-105 shadow-xl'
                  : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
              }`}
            >
              <div className="flex items-center gap-2.5">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-bubble-chart-line text-xl"></i>
                </div>
                <span>Visual Map</span>
              </div>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-7 py-3.5 rounded-full font-bold transition-all text-base whitespace-nowrap shadow-lg ${
                viewMode === 'list'
                  ? 'bg-white text-[#E91E8C] scale-105 shadow-xl'
                  : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
              }`}
            >
              <div className="flex items-center gap-2.5">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-list-check text-xl"></i>
                </div>
                <span>List View</span>
              </div>
            </button>

            {/* Share This Guide Button */}
            <div className="relative">
              <button
                onClick={() => setIsShareOpen(!isShareOpen)}
                className="px-7 py-3.5 rounded-full font-bold transition-all text-base whitespace-nowrap shadow-lg bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm flex items-center gap-2.5"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-share-forward-line text-xl"></i>
                </div>
                <span>Share This Guide</span>
              </button>

              {isShareOpen && (
                <>
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsShareOpen(false)}
                  ></div>

                  {/* Dropdown */}
                  <div className="absolute top-full mt-3 left-1/2 -translate-x-1/2 z-50 bg-white rounded-2xl shadow-2xl p-4 w-64 border border-gray-100 animate-fade-in">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 text-center">Share this guide</p>

                    {/* Facebook */}
                    <button
                      onClick={handleShareFacebook}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-blue-50 transition-colors cursor-pointer group mb-2"
                    >
                      <div className="w-9 h-9 flex items-center justify-center bg-[#1877F2] rounded-full flex-shrink-0">
                        <i className="ri-facebook-fill text-white text-lg"></i>
                      </div>
                      <div className="text-left">
                        <div className="text-sm font-bold text-gray-800 group-hover:text-[#1877F2] transition-colors">Share on Facebook</div>
                        <div className="text-xs text-gray-400">Post to your timeline</div>
                      </div>
                    </button>

                    {/* Twitter / X */}
                    <button
                      onClick={handleShareTwitter}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer group mb-2"
                    >
                      <div className="w-9 h-9 flex items-center justify-center bg-black rounded-full flex-shrink-0">
                        <i className="ri-twitter-x-line text-white text-lg"></i>
                      </div>
                      <div className="text-left">
                        <div className="text-sm font-bold text-gray-800 group-hover:text-black transition-colors">Share on X (Twitter)</div>
                        <div className="text-xs text-gray-400">Tweet to your followers</div>
                      </div>
                    </button>

                    {/* Copy Link */}
                    <button
                      onClick={handleCopyLink}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-pink-50 transition-colors cursor-pointer group"
                    >
                      <div className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 transition-colors ${shareCopied ? 'bg-green-500' : 'bg-[#E91E8C]'}`}>
                        <i className={`text-white text-lg ${shareCopied ? 'ri-check-line' : 'ri-links-line'}`}></i>
                      </div>
                      <div className="text-left">
                        <div className={`text-sm font-bold transition-colors ${shareCopied ? 'text-green-600' : 'text-gray-800 group-hover:text-[#E91E8C]'}`}>
                          {shareCopied ? 'Link Copied!' : 'Copy Link'}
                        </div>
                        <div className="text-xs text-gray-400">{shareCopied ? 'Ready to paste anywhere' : 'Share anywhere you like'}</div>
                      </div>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ✨ Visual Guide — always visible, right under hero */}
      <section className="py-8 bg-white/60">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
            <ConceptMap
              onAgeSelect={handleAgeSelect}
              onCategorySelect={(cat) => handleCategorySelect(cat)}
              selectedAge={selectedAge}
              selectedCategory={selectedCategory}
            />
          </div>
        </div>
      </section>

      {/* Plan Your Summer Calendar */}
      <SummerCalendar selectedWeek={selectedWeek} onWeekSelect={handleWeekSelect} />

      {/* ✨ Cheyenne's Top Picks */}
      <CheyennesTopPicks />

      {/* Browse Section */}
      <section id="browse" className="pb-16">
        <div className="max-w-7xl mx-auto px-6">

          {/* Category Filter Bar */}
          {(selectedAge || viewMode === 'list') && (
            <div
              ref={categoryFiltersRef}
              className="bg-white rounded-2xl shadow-sm px-6 mb-6 scroll-mt-24 border border-gray-100"
            >
              {selectedAge && viewMode === 'map' && (
                <div className="pt-5 pb-2 flex items-center gap-3 border-b border-gray-100">
                  <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-line text-pink-500 text-sm"></i>
                  </div>
                  <span className="text-sm font-bold text-gray-700">
                    {ageGroupLabels[selectedAge]}
                  </span>
                  <button
                    onClick={handleReset}
                    className="ml-auto text-xs text-gray-400 hover:text-pink-500 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Change age group
                  </button>
                </div>
              )}
              <CategoryFilters
                selectedCategory={selectedCategory}
                onCategorySelect={handleCategorySelect}
              />
            </div>
          )}

          {/* Camp Results */}
          {showCampGrid && (
            <div ref={campResultsRef} className="mb-12 scroll-mt-24">
              {/* Search Bar */}
              <div className="mb-5">
                <div className="relative">
                  <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                    <div className="w-5 h-5 flex items-center justify-center text-gray-400">
                      <i className="ri-search-line text-lg"></i>
                    </div>
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search by camp name, location, or activity type…"
                    className="w-full pl-11 pr-10 py-3.5 bg-white border border-gray-200 rounded-2xl text-sm text-gray-800 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-pink-400 transition-all"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute inset-y-0 right-4 flex items-center cursor-pointer text-gray-400 hover:text-pink-500 transition-colors"
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-close-circle-line text-lg"></i>
                      </div>
                    </button>
                  )}
                </div>
              </div>

              {/* Results header */}
              <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    {selectedWeek !== null && !selectedAge && !selectedCategory
                      ? `${summerWeeks.find(w => w.week === selectedWeek)?.label} — ${summerWeeks.find(w => w.week === selectedWeek)?.dates}`
                      : selectedAge && !selectedCategory
                      ? ageGroupLabels[selectedAge] ?? 'Filtered Camps'
                      : selectedAge || selectedCategory || selectedWeek !== null
                      ? 'Filtered Camps'
                      : 'All Camps'}
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5">{filteredCamps.length} camp{filteredCamps.length !== 1 ? 's' : ''} found</p>
                </div>
                {(selectedAge || selectedCategory || selectedWeek !== null) && (
                  <button
                    onClick={handleReset}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full font-semibold hover:bg-gray-200 transition-colors text-sm whitespace-nowrap"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-refresh-line"></i>
                      </div>
                      <span>Reset Filters</span>
                    </div>
                  </button>
                )}
              </div>

              {/* Active filter pills */}
              {(selectedAge || selectedCategory || selectedWeek !== null) && (
                <div className="flex flex-wrap gap-2 mb-5">
                  {selectedWeek !== null && (
                    <span className="inline-flex items-center gap-2 bg-pink-100 text-pink-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                      <i className="ri-calendar-2-line"></i>
                      {summerWeeks.find(w => w.week === selectedWeek)?.label} · {summerWeeks.find(w => w.week === selectedWeek)?.dates}
                      <button onClick={() => setSelectedWeek(null)} className="hover:text-pink-900 cursor-pointer ml-0.5">
                        <i className="ri-close-line"></i>
                      </button>
                    </span>
                  )}
                  {selectedAge && (
                    <span className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                      <i className="ri-user-line"></i>
                      {ageGroupLabels[selectedAge]}
                      <button onClick={() => setSelectedAge(null)} className="hover:text-orange-900 cursor-pointer ml-0.5">
                        <i className="ri-close-line"></i>
                      </button>
                    </span>
                  )}
                  {selectedCategory && (
                    <span className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                      <i className="ri-filter-line"></i>
                      {selectedCategory.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      <button onClick={() => setSelectedCategory(null)} className="hover:text-orange-900 cursor-pointer ml-0.5">
                        <i className="ri-close-line"></i>
                      </button>
                    </span>
                  )}
                </div>
              )}

              {isGroupedView ? (
                <div className="space-y-10">
                  {ageGroupOrder.map((group) => {
                    const groupCamps = filteredCamps.filter(c => c.ageGroup === group);
                    if (groupCamps.length === 0) return null;
                    return (
                      <div key={group}>
                        <div className="flex items-center gap-3 mb-5">
                          <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                            <i className="ri-user-line text-pink-500 text-sm"></i>
                          </div>
                          <h3 className="text-lg font-bold text-gray-800">
                            {ageGroupLabels[group]}
                          </h3>
                          <span className="text-xs text-gray-400 font-medium bg-gray-100 px-2.5 py-1 rounded-full">
                            {groupCamps.length} camp{groupCamps.length !== 1 ? 's' : ''}
                          </span>
                          <div className="flex-1 h-px bg-gray-100 ml-1"></div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                          {groupCamps.map(camp => (
                            <CampCard
                              key={camp.id}
                              camp={camp}
                              onOpenDetail={setSelectedCamp}
                              voteCount={getVotes(camp.id)}
                              hasVoted={hasVoted(camp.id)}
                              onVote={vote}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredCamps.map(camp => (
                    <CampCard
                      key={camp.id}
                      camp={camp}
                      onOpenDetail={setSelectedCamp}
                      voteCount={getVotes(camp.id)}
                      hasVoted={hasVoted(camp.id)}
                      onVote={vote}
                    />
                  ))}
                </div>
              )}

              {filteredCamps.length === 0 && (
                <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gray-50 rounded-full">
                    <i className="ri-search-line text-4xl text-gray-300"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">No camps found</h3>
                  <p className="text-sm text-gray-500 mb-6">Try adjusting your filters to see more results</p>
                  <button
                    onClick={handleReset}
                    className="px-6 py-2.5 bg-pink-500 text-white rounded-full font-semibold hover:bg-pink-600 transition-colors text-sm whitespace-nowrap"
                  >
                    Reset Filters
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#E91E8C] via-[#D946A6] to-[#F5C518]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center mx-auto mb-5 shadow-lg">
            <i className="ri-sun-line text-[#E91E8C] text-3xl"></i>
          </div>
          <h2 className="text-4xl font-bold text-white mb-3">Own a Summer Camp?</h2>
          <p className="text-lg text-white/95 mb-8 max-w-xl mx-auto leading-relaxed">
            Join our community and connect with Gwinnett families looking for the perfect summer experience for their children.
          </p>
          <button
            onClick={() => setIsSubmitModalOpen(true)}
            className="px-8 py-4 bg-white text-[#E91E8C] rounded-full font-bold text-base hover:bg-white/95 hover:scale-105 transition-all shadow-xl whitespace-nowrap"
          >
            Submit Your Camp Today
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#3D1A6E] py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Logo variant="square" scrolled={true} />
                <div>
                  <div className="text-base font-bold text-white" style={{ fontFamily: 'Fredoka, Poppins, system-ui, sans-serif' }}>The Mommy Mingle</div>
                  <div className="text-xs text-[#F5C518] font-semibold" style={{ fontFamily: 'Fredoka, Poppins, system-ui, sans-serif' }}>Summer Camp Guide</div>
                </div>
              </div>
              <p className="text-sm text-white/80 leading-relaxed">
                Connecting families with amazing summer camp experiences in Gwinnett County and surrounding areas.
              </p>
            </div>
            <div>
              <h3 className="text-sm font-bold text-[#F5C518] uppercase tracking-wide mb-4">Quick Links</h3>
              <ul className="space-y-2.5">
                <li><a href="#home" className="text-sm text-white/80 hover:text-[#F5C518] transition-colors">Home</a></li>
                <li><a href="#plan-summer" className="text-sm text-white/80 hover:text-[#F5C518] transition-colors">Plan Your Summer</a></li>
                <li><a href="#browse" className="text-sm text-white/80 hover:text-[#F5C518] transition-colors">Browse Camps</a></li>
                <li>
                  <button onClick={() => setIsSubmitModalOpen(true)} className="text-sm text-white/80 hover:text-[#F5C518] transition-colors cursor-pointer">
                    Submit Your Camp
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-bold text-[#F5C518] uppercase tracking-wide mb-4">Connect With Us</h3>
              <div className="flex gap-3">
                <a href="#" className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#E91E8C] transition-colors shadow-sm text-white">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a href="#" className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#E91E8C] transition-colors shadow-sm text-white">
                  <i className="ri-instagram-line text-lg"></i>
                </a>
                <a href="#" className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#E91E8C] transition-colors shadow-sm text-white">
                  <i className="ri-twitter-line text-lg"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/20 pt-6 text-center">
            <p className="text-sm text-white/60 mb-2">
              © 2026 The Mommy Mingle. All rights reserved. Serving Gwinnett County and surrounding areas.
            </p>
            <p className="text-xs text-white/50 max-w-2xl mx-auto leading-relaxed mb-2">
              Camp details are provided by each organization and may change. Please confirm dates, pricing, and availability directly with the camp.
            </p>
            <p className="text-xs text-white/50 max-w-2xl mx-auto leading-relaxed">
              Built by <span className="font-medium text-white/70">The Mommy Mingle</span> — connecting thousands of local families with over <span className="font-medium text-white/70">30,000 views</span> through our seasonal local resource guides.
            </p>
          </div>
        </div>
      </footer>

      <SubmitCampModal
        isOpen={isSubmitModalOpen}
        onClose={() => setIsSubmitModalOpen(false)}
        formUrl="https://readdy.ai/api/form/d6m6h15v117fnkj2h270"
      />

      <CampDetailModal
        camp={selectedCamp}
        onClose={() => setSelectedCamp(null)}
      />
    </div>
  );
}
